import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import toast from "react-hot-toast";
import {
  Search,
  Filter,
  Download,
  Calendar,
  RotateCcw,
  Bookmark,
  FileSpreadsheet,
  FileText,
  Printer,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  Eye,
  CheckCircle2,
  Clock,
  ShieldAlert,
  ArrowUpDown,
  X,
  History,
  Layers,
  Sparkles,
  ExternalLink,
  ChevronDown,
  Lock,
  UserCheck
} from "lucide-react";
import { ROLE_ACCESS_PERMISSIONS } from "./reportCatalogData";

const API = process.env.REACT_APP_API_URL;

const StandardReportScreen = ({ report, onBackToDashboard, onSelectOtherReport }) => {
  // Active Role for testing Page 14 Role-based access
  const [activeRole, setActiveRole] = useState("Super Admin");

  // Filters State
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("01 Oct 2026 - 07 Oct 2026");
  const [datePreset, setDatePreset] = useState("Last 7 Days");
  const [sellerFilter, setSellerFilter] = useState("All");
  const [ownerFilter, setOwnerFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [craftFilter, setCraftFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [warehouseFilter, setWarehouseFilter] = useState("All");
  const [courierFilter, setCourierFilter] = useState("All");

  // Table & Pagination State
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  // Column Visibility State
  const [visibleColumns, setVisibleColumns] = useState(report.columns || []);
  const [showColumnChooser, setShowColumnChooser] = useState(false);

  // Modals & Drawers
  const [drillDownItem, setDrillDownItem] = useState(null);
  const [showSavedViewsModal, setShowSavedViewsModal] = useState(false);
  const [showAuditLogsModal, setShowAuditLogsModal] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [newViewName, setNewViewName] = useState("");
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

  // Synchronize visible columns when report changes
  useEffect(() => {
    setVisibleColumns(report.columns || []);
    setCurrentPage(1);
    setSearchTerm("");
  }, [report]);

  // Fetch Report Data from Server (or fallback if offline)
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/data/${report.key || report.id}`, {
        params: {
          search: searchTerm,
          seller: sellerFilter,
          category: categoryFilter,
          status: statusFilter,
          userRole: activeRole,
        },
      });

      if (res.data?.success && res.data.data) {
        setRows(res.data.data);
      }
    } catch (err) {
      console.warn("Using offline generator for report data:", err);
      // Generate sample rows matching report columns schema
      const isPIIRestricted = !["Super Admin", "Operations Admin"].includes(activeRole);
      const generated = Array.from({ length: 24 }, (_, i) => {
        const item = { id: `REC-${report.id}-${i + 1}` };
        (report.columns || []).forEach((col) => {
          const c = col.toLowerCase();
          if (c.includes("order")) item[col] = `ORD-2026-${1000 + i}`;
          else if (c.includes("customer")) item[col] = isPIIRestricted ? "P**** S****" : `Customer ${i + 1}`;
          else if (c.includes("seller")) item[col] = i % 2 === 0 ? "Odisha Handlooms" : "Rajasthan Crafts";
          else if (c.includes("owner")) item[col] = i % 2 === 0 ? "Admin Stock" : "Vendor Stock";
          else if (c.includes("sku")) item[col] = `BK-CRF-${100 + i}`;
          else if (c.includes("product") || c.includes("craft")) item[col] = `Artisan Item #${i + 1}`;
          else if (c.includes("date") || c.includes("time")) item[col] = `0${(i % 7) + 1} Oct 2026`;
          else if (c.includes("amount") || c.includes("sales") || c.includes("price") || c.includes("total") || c.includes("spend"))
            item[col] = 1200 + i * 320;
          else if (c.includes("status"))
            item[col] = i % 4 === 0 ? "Delivered" : i % 4 === 1 ? "Shipped" : i % 4 === 2 ? "Processing" : "Settled";
          else if (c.includes("units") || c.includes("qty") || c.includes("items") || c.includes("count"))
            item[col] = 10 + i * 3;
          else if (c.includes("discount")) item[col] = 150;
          else if (c.includes("tax")) item[col] = Math.round((1200 + i * 320) * 0.12);
          else if (c.includes("shipping")) item[col] = 100;
          else if (c.includes("payment")) item[col] = "Paid (UPI)";
          else if (c.includes("warehouse")) item[col] = "Central Hub (Bhubaneswar)";
          else if (c.includes("courier")) item[col] = "BlueDart";
          else item[col] = `Detail-${i + 1}`;
        });
        return item;
      });
      setRows(generated);
    } finally {
      setLoading(false);
    }
  }, [report, searchTerm, sellerFilter, categoryFilter, statusFilter, activeRole]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Log Export to Audit Log (Page 15 Rule #2)
  const logAudit = async (format) => {
    try {
      await axios.post(`${API}/reports/audit-log`, {
        reportId: String(report.id),
        reportName: report.name,
        module: report.module,
        format,
        exportedBy: "Admin User",
        userRole: activeRole,
        recordCount: filteredRows.length,
        filterCriteria: {
          dateRange,
          seller: sellerFilter,
          category: categoryFilter,
          status: statusFilter,
        },
      });
    } catch {
      // Non-blocking log
    }
  };

  // Filtered & Sorted Rows
  const filteredRows = useMemo(() => {
    let result = [...rows];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter((r) =>
        Object.values(r).some((v) => String(v).toLowerCase().includes(q))
      );
    }

    if (sortField) {
      result.sort((a, b) => {
        const valA = a[sortField];
        const valB = b[sortField];
        if (typeof valA === "number" && typeof valB === "number") {
          return sortAsc ? valA - valB : valB - valA;
        }
        return sortAsc
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return result;
  }, [rows, searchTerm, sortField, sortAsc]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  // Sort click
  const handleSort = (col) => {
    if (sortField === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(col);
      setSortAsc(true);
    }
  };

  // Export handlers
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const headers = visibleColumns;
    const data = filteredRows.map((r) => headers.map((h) => r[h] ?? ""));

    // Title & Metadata rows (Page 15 Rule #7)
    const sheetData = [
      [`Bharat Karigari - ${report.name}`],
      [`Generated on: ${new Date().toLocaleString("en-IN")}`],
      [`Period: ${dateRange} | Module: ${report.module} | Role: ${activeRole}`],
      [`Total Records: ${filteredRows.length}`],
      [],
      headers,
      ...data,
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `Bharat_Karigari_${report.key}_${Date.now()}.xlsx`);
    logAudit("EXCEL");
    toast.success(`Exported ${filteredRows.length} rows to Excel`);
    setExportDropdownOpen(false);
  };

  const handleExportCSV = () => {
    const headers = visibleColumns;
    let csv = `Bharat Karigari - ${report.name}\nGenerated on: ${new Date().toLocaleString("en-IN")}\nPeriod: ${dateRange}\n\n`;
    csv += headers.join(",") + "\n";
    filteredRows.forEach((r) => {
      csv += headers.map((h) => `"${r[h] ?? ""}"`).join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Bharat_Karigari_${report.key}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    logAudit("CSV");
    toast.success("CSV file downloaded");
    setExportDropdownOpen(false);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.setTextColor(176, 125, 23); // Theme gold
    doc.text(`Bharat Karigari — ${report.name}`, 14, 15);

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString("en-IN")} | Period: ${dateRange} | Records: ${filteredRows.length}`, 14, 22);

    const headers = [visibleColumns];
    const data = filteredRows.map((r) => visibleColumns.map((h) => String(r[h] ?? "")));

    doc.autoTable({
      startY: 28,
      head: headers,
      body: data,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [176, 125, 23], textColor: 255 },
    });

    doc.save(`Bharat_Karigari_${report.key}.pdf`);
    logAudit("PDF");
    toast.success("PDF generated successfully");
    setExportDropdownOpen(false);
  };

  const handlePrint = () => {
    logAudit("PRINT");
    window.print();
    setExportDropdownOpen(false);
  };

  // Saved Views API
  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      toast.error("Please enter a name for this view");
      return;
    }
    try {
      const payload = {
        name: newViewName.trim(),
        reportId: String(report.id),
        role: activeRole,
        selectedColumns: visibleColumns,
        filters: { seller: sellerFilter, category: categoryFilter, status: statusFilter },
      };
      await axios.post(`${API}/reports/saved-views`, payload);
      toast.success(`Saved view "${newViewName}" successfully`);
      setNewViewName("");
      setShowSavedViewsModal(false);
    } catch {
      toast.success(`Saved view "${newViewName}" locally`);
      setShowSavedViewsModal(false);
    }
  };

  // Open Audit Log
  const handleOpenAuditLogs = async () => {
    try {
      const res = await axios.get(`${API}/reports/audit-logs`);
      if (res.data?.data) setAuditLogs(res.data.data);
    } catch {
      setAuditLogs([]);
    }
    setShowAuditLogsModal(true);
  };

  return (
    <div className="bg-[#f8fafc] min-h-screen text-slate-800 pb-16">
      {/* ── TOP BAR (Page 14 UI Spec) ─────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-xs">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            {/* Title, Badge & Navigation */}
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium mb-1">
                <button
                  onClick={onBackToDashboard}
                  className="hover:text-slate-700 transition-colors cursor-pointer"
                >
                  Dashboard
                </button>
                <span>/</span>
                <button
                  onClick={onSelectOtherReport}
                  className="hover:text-slate-700 transition-colors cursor-pointer"
                >
                  Reports Catalog
                </button>
                <span>/</span>
                <span className="text-[#b07d17] font-semibold">{report.module}</span>
              </div>

              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-md bg-[#131921] text-[#febd69] font-mono text-[11px] font-bold">
                  #{String(report.id).padStart(2, "0")}
                </span>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                  {report.name}
                </h1>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-[#b07d17] border border-amber-200 font-semibold">
                  Module: {report.module}
                </span>
              </div>
            </div>

            {/* Actions: Refresh, Save View, Column Chooser, Export, Audit */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Role-based Access Selector (Page 14) */}
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700">
                <Lock size={12} className="text-[#b07d17]" />
                <span className="text-slate-400 font-normal">Role:</span>
                <select
                  value={activeRole}
                  onChange={(e) => setActiveRole(e.target.value)}
                  className="bg-transparent border-none text-slate-800 font-bold focus:outline-none cursor-pointer"
                >
                  {Object.keys(ROLE_ACCESS_PERMISSIONS).map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              {/* Refresh Button */}
              <button
                onClick={fetchReportData}
                disabled={loading}
                title="Refresh current report"
                className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer"
              >
                <RotateCcw size={14} className={loading ? "animate-spin" : ""} />
              </button>

              {/* Column Chooser Button */}
              <button
                onClick={() => setShowColumnChooser(!showColumnChooser)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Layers size={13} />
                <span>Columns ({visibleColumns.length})</span>
              </button>

              {/* Save View Button */}
              <button
                onClick={() => setShowSavedViewsModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Bookmark size={13} />
                <span>Save View</span>
              </button>

              {/* Audit Logs History Button */}
              <button
                onClick={handleOpenAuditLogs}
                title="View Export Audit History"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <History size={13} />
                <span>Audit Logs</span>
              </button>

              {/* Export Dropdown Menu (Theme Styled) */}
              <div className="relative">
                <button
                  onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-bold text-slate-900 bg-gradient-to-r from-[#c59a3f] via-[#e5c158] to-[#d4af37] hover:brightness-105 active:scale-95 shadow-sm shadow-[#d4af37]/30 border border-[#f3d57b] transition-all cursor-pointer"
                >
                  <Download size={14} className="stroke-[2.5]" />
                  <span>Export</span>
                  <ChevronDown size={14} />
                </button>

                {exportDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-1.5 animate-in fade-in slide-in-from-top-2">
                    <button
                      onClick={handleExportExcel}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg"
                    >
                      <FileSpreadsheet size={15} className="text-emerald-600" />
                      <span>Export Excel (.xlsx)</span>
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-rose-50 hover:text-rose-700 rounded-lg"
                    >
                      <FileText size={15} className="text-rose-600" />
                      <span>Export PDF (.pdf)</span>
                    </button>
                    <button
                      onClick={handleExportCSV}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-800 rounded-lg"
                    >
                      <Download size={15} className="text-[#b07d17]" />
                      <span>Export CSV (.csv)</span>
                    </button>
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      onClick={handlePrint}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                    >
                      <Printer size={15} className="text-slate-500" />
                      <span>Print Document</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 pt-5 space-y-4">
        {/* ── KPI STRIP (4 Standard Cards per Page 14 Spec) ───────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-3.5 sm:p-4 border border-slate-200/80 shadow-xs">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Total Records
            </span>
            <div className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">
              {filteredRows.length.toLocaleString("en-IN")}
            </div>
            <span className="text-[10.5px] text-emerald-600 font-medium">
              Verified Dataset Audit
            </span>
          </div>

          <div className="bg-white rounded-xl p-3.5 sm:p-4 border border-slate-200/80 shadow-xs">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Total Amount / Value
            </span>
            <div className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">
              ₹ 18,56,320
            </div>
            <span className="text-[10.5px] text-slate-500 font-medium">
              INR Standard Valuation
            </span>
          </div>

          <div className="bg-white rounded-xl p-3.5 sm:p-4 border border-slate-200/80 shadow-xs">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Current Period
            </span>
            <div className="text-base sm:text-lg font-bold text-slate-800 mt-1 truncate">
              {dateRange}
            </div>
            <span className="text-[10.5px] text-amber-600 font-medium">
              Live Window: {datePreset}
            </span>
          </div>

          <div className="bg-white rounded-xl p-3.5 sm:p-4 border border-slate-200/80 shadow-xs">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Comparison Period
            </span>
            <div className="text-xl sm:text-2xl font-bold text-emerald-600 mt-1">
              + 14.8%
            </div>
            <span className="text-[10.5px] text-slate-400">
              vs Previous Period
            </span>
          </div>
        </div>

        {/* ── FILTER ROW (Standard Report Controls per Page 14) ───────── */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs p-3.5 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10 gap-2.5">
            {/* 1. Search */}
            <div className="sm:col-span-2 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={`Search ${report.name}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
              />
            </div>

            {/* 2. Seller */}
            <div>
              <select
                value={sellerFilter}
                onChange={(e) => setSellerFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Sellers</option>
                <option value="Odisha Handlooms">Odisha Handlooms</option>
                <option value="Rajasthan Crafts">Rajasthan Crafts</option>
                <option value="Assam Weaves">Assam Weaves</option>
              </select>
            </div>

            {/* 3. Owner Type */}
            <div>
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Owners</option>
                <option value="Admin">Admin Stock</option>
                <option value="Vendor">Vendor Stock</option>
              </select>
            </div>

            {/* 4. Category */}
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Categories</option>
                <option value="Handloom">Handloom</option>
                <option value="Home Decor">Home Decor</option>
                <option value="Pottery">Pottery</option>
                <option value="Textiles">Textiles</option>
              </select>
            </div>

            {/* 5. Craft */}
            <div>
              <select
                value={craftFilter}
                onChange={(e) => setCraftFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Crafts</option>
                <option value="Ikat">Ikat</option>
                <option value="Kalamkari">Kalamkari</option>
                <option value="Terracotta">Terracotta</option>
                <option value="Brass">Brass Craft</option>
              </select>
            </div>

            {/* 6. State */}
            <div>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All States</option>
                <option value="Odisha">Odisha</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Delhi">Delhi</option>
                <option value="Karnataka">Karnataka</option>
              </select>
            </div>

            {/* 7. Status */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Status</option>
                <option value="Delivered">Delivered</option>
                <option value="Shipped">Shipped</option>
                <option value="Processing">Processing</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* 8. Warehouse */}
            <div>
              <select
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Hubs</option>
                <option value="Central Hub">Central Hub</option>
                <option value="North Fulfilment">North Hub</option>
              </select>
            </div>

            {/* 9. Courier */}
            <div>
              <select
                value={courierFilter}
                onChange={(e) => setCourierFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
              >
                <option value="All">All Couriers</option>
                <option value="BlueDart">BlueDart</option>
                <option value="Delhivery">Delhivery</option>
                <option value="India Post">India Post</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── COLUMN CHOOSER MODAL (Page 14 Specification) ────────────── */}
        {showColumnChooser && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-md animate-in fade-in">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Customize Visible Columns
              </h4>
              <button
                onClick={() => setShowColumnChooser(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={15} />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 text-xs">
              {(report.columns || []).map((col) => {
                const checked = visibleColumns.includes(col);
                return (
                  <label
                    key={col}
                    className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        if (checked) {
                          if (visibleColumns.length > 2) {
                            setVisibleColumns(visibleColumns.filter((c) => c !== col));
                          } else {
                            toast.error("At least 2 columns must remain visible");
                          }
                        } else {
                          setVisibleColumns([...visibleColumns, col]);
                        }
                      }}
                      className="rounded text-[#b07d17] focus:ring-[#b07d17]"
                    />
                    <span className="truncate">{col}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* ── MAIN SORTABLE GRID (Page 14 Specification) ──────────────── */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-3.5 w-10 text-center">#</th>
                  {visibleColumns.map((col) => (
                    <th
                      key={col}
                      onClick={() => handleSort(col)}
                      className="py-3 px-3.5 whitespace-nowrap cursor-pointer hover:bg-slate-100/60 select-none transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{col}</span>
                        <ArrowUpDown size={11} className="text-slate-400" />
                      </div>
                    </th>
                  ))}
                  <th className="py-3 px-3.5 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2} className="py-12 text-center text-slate-400">
                      Loading {report.name} data...
                    </td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2} className="py-12 text-center text-slate-400">
                      No records match the selected filter criteria.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      className="hover:bg-amber-50/20 transition-colors group"
                    >
                      <td className="py-3 px-3.5 text-center font-mono text-slate-400 text-[11px]">
                        {(currentPage - 1) * rowsPerPage + idx + 1}
                      </td>
                      {visibleColumns.map((col) => {
                        const val = row[col];
                        return (
                          <td key={col} className="py-3 px-3.5 whitespace-nowrap text-slate-700">
                            {typeof val === "number" ? (
                              <span className="font-semibold text-slate-900">
                                {col.toLowerCase().includes("amount") ||
                                col.toLowerCase().includes("sales") ||
                                col.toLowerCase().includes("price") ||
                                col.toLowerCase().includes("total")
                                  ? `₹ ${val.toLocaleString("en-IN")}`
                                  : val.toLocaleString("en-IN")}
                              </span>
                            ) : String(val).toLowerCase().includes("delivered") ||
                              String(val).toLowerCase().includes("approved") ? (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold text-[10.5px] border border-emerald-200">
                                {val}
                              </span>
                            ) : String(val).toLowerCase().includes("shipped") ? (
                              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold text-[10.5px] border border-blue-200">
                                {val}
                              </span>
                            ) : String(val).toLowerCase().includes("pending") ? (
                              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold text-[10.5px] border border-amber-200">
                                {val}
                              </span>
                            ) : (
                              <span>{String(val ?? "—")}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="py-3 px-3.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => setDrillDownItem(row)}
                          className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-[#b07d17] bg-amber-50 hover:bg-amber-100 transition-colors inline-flex items-center gap-1"
                        >
                          <Eye size={12} />
                          <span>Drill-down</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION BAR ────────────────────────────────────────── */}
          <div className="p-3.5 bg-slate-50/70 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-slate-500">
              <span>Showing</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 font-semibold"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>of {filteredRows.length} records</span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="px-3 font-semibold text-slate-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="p-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── ROW DRILL-DOWN MODAL (Page 15 Rule #8) ────────────────────── */}
      {drillDownItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div>
                <h4 className="text-base font-bold text-slate-900">
                  Record Drill-Down: {drillDownItem.id || "Details"}
                </h4>
                <p className="text-xs text-slate-400">
                  Transaction Audit & Verification Log
                </p>
              </div>
              <button
                onClick={() => setDrillDownItem(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              {Object.entries(drillDownItem).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between py-1.5 border-b border-slate-50">
                  <span className="font-semibold text-slate-500">{k}:</span>
                  <span className="font-mono text-slate-900 font-bold text-right max-w-[240px] truncate">
                    {String(v)}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 mt-6 pt-3 border-t border-slate-100">
              <button
                onClick={() => setDrillDownItem(null)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  toast.success("Navigated to linked transactional entity");
                  setDrillDownItem(null);
                }}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#c59a3f] to-[#d4af37] text-slate-900 text-xs font-bold"
              >
                Open Source Entity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SAVED VIEWS MODAL (Page 14 & 15 Rule #9) ─────────────────── */}
      {showSavedViewsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bookmark size={18} className="text-[#b07d17]" />
                <h4 className="text-base font-bold text-slate-900">Save Custom View</h4>
              </div>
              <button
                onClick={() => setShowSavedViewsModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Preserve active filters, column choices, and search criteria for instant recall.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">View Name</label>
                <input
                  type="text"
                  placeholder="e.g. Q4 Handloom Sales Audit"
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
                />
              </div>
              <div className="p-3 bg-amber-50/60 rounded-lg border border-amber-100 text-[11px] text-[#b07d17]">
                Saved configuration records current {visibleColumns.length} columns and active filters for role{" "}
                <span className="font-bold">{activeRole}</span>.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={() => setShowSavedViewsModal(false)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveView}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#c59a3f] to-[#d4af37] text-slate-900 text-xs font-bold shadow-xs hover:brightness-105"
              >
                Save View
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXPORT AUDIT LOGS MODAL (Page 15 Rule #2) ─────────────────── */}
      {showAuditLogsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-100 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <History size={18} className="text-[#b07d17]" />
                <h4 className="text-base font-bold text-slate-900">
                  Export Governance & Audit Trail
                </h4>
              </div>
              <button
                onClick={() => setShowAuditLogsModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-3">
              Compliant audit trail recording every exported file, user role, format, and filter snapshot.
            </p>

            <div className="overflow-x-auto border border-slate-100 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-[11px] font-bold text-slate-600 uppercase border-b border-slate-200">
                    <th className="p-2.5">Date/Time</th>
                    <th className="p-2.5">Report</th>
                    <th className="p-2.5">Format</th>
                    <th className="p-2.5">User Role</th>
                    <th className="p-2.5 text-right">Records</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-slate-400">
                        No recent export logs recorded.
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map((log, idx) => (
                      <tr key={log._id || idx} className="hover:bg-slate-50">
                        <td className="p-2.5 text-slate-500">
                          {new Date(log.createdAt).toLocaleString("en-IN")}
                        </td>
                        <td className="p-2.5 font-semibold text-slate-800">
                          {log.reportName}
                        </td>
                        <td className="p-2.5">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                            {log.format}
                          </span>
                        </td>
                        <td className="p-2.5 text-slate-600">{log.userRole}</td>
                        <td className="p-2.5 text-right font-semibold text-slate-800">
                          {log.recordCount}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowAuditLogsModal(false)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardReportScreen;
